import { useEffect, useMemo, useState } from 'react'
import { useDisplayUserError } from '@yes.technology/react-toolkit'
import {
  AvailableCredentials,
  CredentialItem,
  Credentials
} from 'authentication/shared/types/authentication.type'
import { useAuthentication } from 'authentication/shared/hooks'
import useUpgradeToken from 'authentication/shared/hooks/useUpgradeToken'
import { useQuery } from '@tanstack/react-query'
import { fetchCredentials } from 'authentication/shared/api'
import tryJwtDecode from 'shared/utils/jwt'

type Option = {
  id: string
  value: string
  label: string
}

const formatCredentialsOptions = (
  optionsUuids: CredentialItem[] | null | undefined,
  withSID: boolean
): Option[] => {
  if (!optionsUuids) {
    return []
  }

  return optionsUuids.map(({ uuid, sid, des }) => ({
    id: uuid,
    value: uuid,
    label: withSID ? `${sid} |  ${des}` : des
  }))
}

const getApplicationSystemOptions = (
  availableCredentials: AvailableCredentials,
  selectedCredentials: Credentials
): Option[] => {
  let applicationSystemOptions =
    availableCredentials?.applicationsystems_info || []
  const credentialList = availableCredentials?.credential_list

  if (credentialList) {
    applicationSystemOptions = applicationSystemOptions.filter(
      (applicationSystem) => {
        if (selectedCredentials.organization_uuid) {
          return credentialList.some(
            (credential) =>
              credential.organization_uuid ===
                selectedCredentials.organization_uuid &&
              credential.applicationsystem_uuid === applicationSystem.uuid
          )
        }
        if (selectedCredentials.user_uuid) {
          return credentialList.some(
            (credential) =>
              credential.user_uuid === selectedCredentials.user_uuid &&
              credential.applicationsystem_uuid === applicationSystem.uuid
          )
        }
        return true
      }
    )
  }

  return formatCredentialsOptions(applicationSystemOptions, false)
}

const getOrganizationOptions = (
  availableCredentials: AvailableCredentials,
  selectedCredentials: Credentials
): Option[] => {
  let organizationOptions = availableCredentials?.organizations_info || []
  const credentialList = availableCredentials?.credential_list

  if (credentialList) {
    organizationOptions = organizationOptions.filter((organization) => {
      if (selectedCredentials.applicationsystem_uuid) {
        return credentialList.some(
          (credential) =>
            credential.applicationsystem_uuid ===
              selectedCredentials.applicationsystem_uuid &&
            credential.organization_uuid === organization.uuid
        )
      }
      if (selectedCredentials.user_uuid) {
        return credentialList.some(
          (credential) =>
            credential.user_uuid === selectedCredentials.user_uuid &&
            credential.organization_uuid === organization.uuid
        )
      }
      return true
    })
  }

  return formatCredentialsOptions(organizationOptions, true)
}

const getUserOptions = (
  availableCredentials: AvailableCredentials,
  selectedCredentials: Credentials
): Option[] => {
  let userOptions = availableCredentials?.user_info || []
  const credentialList = availableCredentials?.credential_list

  if (credentialList) {
    userOptions = userOptions.filter((user) => {
      if (selectedCredentials.applicationsystem_uuid) {
        return credentialList.some(
          (credential) =>
            credential.applicationsystem_uuid ===
              selectedCredentials.applicationsystem_uuid &&
            credential.user_uuid === user.uuid
        )
      }
      if (selectedCredentials.organization_uuid) {
        return credentialList.some(
          (credential) =>
            credential.organization_uuid ===
              selectedCredentials.organization_uuid &&
            credential.user_uuid === user.uuid
        )
      }
      return true
    })
  }

  return formatCredentialsOptions(userOptions, true)
}

const useCredentials = () => {
  const { authenticatedData, startLoading, stopLoading } = useAuthentication()

  const upgradeToken = useUpgradeToken()

  const { displayUserError } = useDisplayUserError()

  const [credentialsLoaded, setCredentialsLoaded] = useState(false)

  const { token } = authenticatedData

  const { user_id: userUuid, credentials: tokenCredentials } =
    tryJwtDecode<{ user_id: string; credentials: Credentials }>(token || '') ||
    {}

  const {
    data: fetchedAvailableCredentials,
    isFetching: isFetchingCredentials,
    isFetched,
    error
  } = useQuery<AvailableCredentials | null, Error>({
    queryKey: ['filteraction', userUuid],
    queryFn: () => fetchCredentials(userUuid as string),
    enabled: !authenticatedData.available_credentials && !!userUuid
  })

  useEffect(() => {
    if (isFetched && error) {
      displayUserError({ error })
      console.log(error)
    }
  }, [error, displayUserError, isFetched])

  const [selectedCredentials, setSelectedCredentials] = useState<Credentials>({
    applicationsystem_uuid: tokenCredentials?.applicationsystem_uuid || '',
    organization_uuid: tokenCredentials?.organization_uuid || '',
    user_uuid: tokenCredentials?.user_uuid || ''
  })

  const credentials = useMemo(() => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const applicationSystemOptions = getApplicationSystemOptions(
      availableCredentials,
      selectedCredentials
    )

    const organizationOptions = getOrganizationOptions(
      availableCredentials,
      selectedCredentials
    )

    const userOptions = getUserOptions(
      availableCredentials,
      selectedCredentials
    )

    return {
      applicationSystem: {
        options: applicationSystemOptions
      },
      organization: {
        options: organizationOptions
      },
      user: {
        options: userOptions
      }
    }
  }, [authenticatedData, fetchedAvailableCredentials, selectedCredentials])

  const submitCredentials = async (
    onSuccess: (upgradedToken: string) => void,
    onError: (error?: unknown) => void
  ) => {
    startLoading()

    try {
      const upgradedToken = await upgradeToken(selectedCredentials)
      upgradedToken && onSuccess?.(upgradedToken)
    } catch (error) {
      onError?.(error)
      displayUserError({ error })
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const setEcosystem = (ecosystemUuid: string) => {
    setSelectedCredentials((prev) => ({
      ...prev,
      ecosystem_uuid: ecosystemUuid
    }))
  }

  const setApplicationSystem = (applicationSystemUuid: string) => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const organizationOptions = getOrganizationOptions(availableCredentials, {
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: '',
      user_uuid: selectedCredentials.user_uuid
    })

    const organizationUuid =
      applicationSystemUuid && organizationOptions.length === 1
        ? organizationOptions[0].id
        : selectedCredentials.organization_uuid

    const userOptions = getUserOptions(availableCredentials, {
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: ''
    })

    const userUuid =
      applicationSystemUuid && userOptions.length === 1
        ? userOptions[0].id
        : selectedCredentials.user_uuid

    setSelectedCredentials({
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }

  const setOrganization = (organizationUuid: string) => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const applicationOptions = getApplicationSystemOptions(
      availableCredentials,
      {
        applicationsystem_uuid: '',
        organization_uuid: organizationUuid,
        user_uuid: selectedCredentials.user_uuid
      }
    )

    const applicationSystemUuid =
      organizationUuid && applicationOptions.length === 1
        ? applicationOptions[0].id
        : selectedCredentials.applicationsystem_uuid

    const userOptions = getUserOptions(availableCredentials, {
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: ''
    })

    const userUuid =
      organizationUuid && userOptions.length === 1
        ? userOptions[0].id
        : selectedCredentials.user_uuid

    setSelectedCredentials({
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }

  const setUser = (userUuid: string) => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const applicationOptions = getApplicationSystemOptions(
      availableCredentials,
      {
        applicationsystem_uuid: '',
        organization_uuid: selectedCredentials.organization_uuid,
        user_uuid: userUuid
      }
    )

    const applicationSystemUuid =
      userUuid && applicationOptions.length === 1
        ? applicationOptions[0].id
        : selectedCredentials.applicationsystem_uuid

    const organizationOptions = getOrganizationOptions(availableCredentials, {
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: '',
      user_uuid: selectedCredentials.user_uuid
    })

    const organizationUuid =
      userUuid && organizationOptions.length === 1
        ? organizationOptions[0].id
        : selectedCredentials.organization_uuid

    setSelectedCredentials({
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }

  const isCredentialsValid = useMemo(() => {
    return (
      selectedCredentials.applicationsystem_uuid &&
      selectedCredentials.organization_uuid &&
      selectedCredentials.user_uuid
    )
  }, [selectedCredentials])

  // select credentials if there is only one option
  useEffect(() => {
    if (credentialsLoaded || !credentials) return

    setCredentialsLoaded(true)

    const appOptions = credentials.applicationSystem.options
    const orgOptions = credentials.organization.options
    const userOptions = credentials.user.options
    const applicationUuid = appOptions.length === 1 ? appOptions[0].id : ''
    const organizationUuid = orgOptions.length === 1 ? orgOptions[0].id : ''
    const userUuid = userOptions.length === 1 ? userOptions[0].id : ''

    setSelectedCredentials({
      applicationsystem_uuid: applicationUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }, [credentials, credentialsLoaded])

  return {
    credentials,
    selectedCredentials,
    isCredentialsValid,
    isLoading: isFetchingCredentials,
    submitCredentials,
    setEcosystem,
    setApplicationSystem,
    setOrganization,
    setUser
  }
}

export default useCredentials
